import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  // Receipt as ReceiptIcon,
  Settings as SettingsIcons,
} from "@material-ui/icons";
import { Navigation, SkeletonBodyText } from "@shopify/polaris";
import { CaretDownMinor, ConversationMinor } from "@shopify/polaris-icons";

import { useTheme } from "@material-ui/core";
import { useAdmin } from "context/admin";
import { useUser } from "context/user";

function ShopLinks({ shops, selectedShopId, handleNavigate, user, location }) {
  const theme = useTheme();

  // Core store links
  let navbarLinks = [
    {
      label: "Orders",
      icon: SettingsIcons,
      onClick: () => handleNavigate(`/shops/${selectedShopId}/orders`),
    },
  ];

  if (user && !user.isVendor) {
    navbarLinks = [
      ...navbarLinks,
      {
        label: "Deliveries",
        icon: SettingsIcons,
        onClick: () => handleNavigate(`/shops/${selectedShopId}/deliveries`),
      },
      {
        label: "Payment Report",
        icon: SettingsIcons,
        onClick: () => handleNavigate(`/shops/${selectedShopId}/payment-report`),
      },
      {
        label: "Settings",
        icon: SettingsIcons,
        onClick: () => handleNavigate(`/shops/${selectedShopId}/settings`),
      },
    ];
  }

  if (user && (user.manualDeliveryAccess || user.isAdmin)) {
    navbarLinks = [
      ...navbarLinks,
      {
        label: "Manual Deliveries",
        icon: SettingsIcons,
        onClick: () =>
          handleNavigate(`/shops/${selectedShopId}/manual-deliveries`),
      },
    ];
  }

  if (user && user.isAdmin) {
    navbarLinks = [
      ...navbarLinks,
      {
        label: "Users",
        icon: SettingsIcons,
        onClick: () => handleNavigate(`/shops/${selectedShopId}/users`),
      },
      {
        label: "Sales",
        icon: SettingsIcons,
        onClick: () => handleNavigate(`/shops/${selectedShopId}/sales`),
      },
    ];
  }

  // Products sub-section items
  // - hasProductAccess: full product link list (catalog/insights/imports + Weight Options if allowed)
  // - canSeeWeightOptions (catalog role or admin): can reach Weight Options even
  //   without import access. Used as a standalone Products section in that case.
  const hasProductAccess = user && (user.importAccess || user.isAdmin);
  const canSeeWeightOptions = user && (user.isAdmin || user.catalogAccess);

  const weightOptionsLink = {
    label: "Weight Options",
    icon: SettingsIcons,
    onClick: () => handleNavigate(`/shops/${selectedShopId}/weight-options`),
  };

  let productsLinks = [];
  if (hasProductAccess) {
    productsLinks = [
      {
        label: "Catalog",
        icon: SettingsIcons,
        onClick: () =>
          handleNavigate(`/shops/${selectedShopId}/products/catalog`),
      },
      {
        label: "Insights",
        icon: SettingsIcons,
        onClick: () =>
          handleNavigate(`/shops/${selectedShopId}/products/insights`),
      },
      {
        label: "File Imports",
        icon: SettingsIcons,
        onClick: () =>
          handleNavigate(`/shops/${selectedShopId}/file-imports`),
      },
      {
        label: "Import History",
        icon: SettingsIcons,
        onClick: () =>
          handleNavigate(`/shops/${selectedShopId}/products/import-history`),
      },
      ...(canSeeWeightOptions ? [weightOptionsLink] : []),
    ];
  } else if (canSeeWeightOptions) {
    productsLinks = [weightOptionsLink];
  }

  const showProductsSection = productsLinks.length > 0;

  return shops.map((shop) => {
    const isSelected =
      selectedShopId != null && String(selectedShopId) === String(shop.id);

    if (!isSelected) {
      return (
        <Navigation.Section
          key={shop.id}
          title={shop.current.name}
          items={[]}
          data-cy="navbarBtn"
          action={{
            icon: CaretDownMinor,
            accessibilityLabel: "View Shop",
            onClick: () => handleNavigate(`/shops/${shop.id}`),
          }}
        />
      );
    }

    return (
      <React.Fragment key={shop.id}>
        {/* Core store navigation */}
        <Navigation.Section
          separator
          title={
            <span style={{ color: theme.palette.primary.main }}>
              {shop.current.name}
            </span>
          }
          items={navbarLinks}
        />

        {/* Products group — visually grouped with the store section */}
        {showProductsSection && (
          <Navigation.Section
            title="Products"
            items={productsLinks}
          />
        )}
      </React.Fragment>
    );
  });
}

export default function DashboardSidebar({ onNavigationDismiss }) {
  const admin = useAdmin();
  const user = useUser();
  const history = useHistory();
  const location = useLocation();

  // Fallback: extract shopId from URL when admin context hasn't updated yet
  const shopIdFromPath = location.pathname.match(/^\/shops\/([^/]+)/)?.[1];
  const effectiveSelectedShopId =
    admin.selectedShopId ??
    shopIdFromPath ??
    (admin.selectedShop && admin.selectedShop.id);

  // For Shop filter
  // const [shopFilter, setShopFilter] = useState();
  // {user && user.isAdmin ?
  //   <TextField
  //     label="Filter Shops"
  //     value={shopFilter}
  //     onChange={(value) => setShopFilter(value)}
  //     clearButton
  //     onClearButtonClick={() => setShopFilter('')}
  //   />
  // : ''}

  const navigate = (path) => {
    history.push(path);
    if (onNavigationDismiss) onNavigationDismiss();
  };

  const shopLinks = admin.shops ? (
    <ShopLinks
      shops={admin.shops}
      selectedShopId={effectiveSelectedShopId}
      handleNavigate={navigate}
      user={user}
      location={location.pathname}
    />
  ) : (
    ""
  );

  const adminLinks =
    user && user.isAdmin ? (
      <Navigation.Section
        separator
        title="Admin"
        items={[
          {
            label: "Dashboard",
            icon: SettingsIcons,
            onClick: () => navigate(`/admin/dashboard`),
          },
          {
            label: "Deliveries",
            icon: SettingsIcons,
            onClick: () => navigate(`/admin/deliveries`),
          },
          {
            label: "Users",
            icon: SettingsIcons,
            onClick: () => navigate(`/admin/users`),
          },
          {
            label: "Register Store",
            icon: SettingsIcons,
            onClick: () => navigate(`/admin/shops/register`),
          },
          {
            label: "Bottle Deposits",
            icon: SettingsIcons,
            onClick: () => navigate(`/admin/bottle-deposits`),
          },
        ]}
      />
    ) : (
      ""
    );

  const supportLinks = false ? (
    <Navigation.Section
      separator
      title="Support"
      items={[]}
      action={{
        icon: ConversationMinor,
        accessibilityLabel: "Contact support",
        onClick: () => console.log("Contact support"),
      }}
    />
  ) : (
    ""
  );

  const loadingShops = (
    <div style={{ padding: "2rem" }}>
      <SkeletonBodyText />
    </div>
  );

  return (
    <Navigation location={location.pathname} onDismiss={onNavigationDismiss}>
      {admin.loadingShops ? loadingShops : shopLinks}
      {supportLinks}
      {adminLinks}
    </Navigation>
  );
}
